
import { Vue, Component, Ref } from 'vue-property-decorator';
import lazyInject from '@/di';
import RegionService from '@/services/region.service';
import { IResRegion } from '@/common/interface/region';
import SupplyService from '@/services/supply.service';
import GeneratePurchaseOrderModal from './GeneratePurchaseOrderModal.vue';

@Component({
  components: {
    GeneratePurchaseOrderModal,
  },
})
export default class GeneratePurchaseOrderDrawer extends Vue {
  visible = false;

  loading = false;

  filterForm: any = {
    platformSalesOrderNo: '',
    province: undefined,
    dealer: '',
    timeRange: [],
  };

  provinces: IResRegion[] = [];

  @lazyInject(RegionService)
  protected regionService!: RegionService;

  @lazyInject(SupplyService)
  protected supplyService!: SupplyService;

  dataSource: any[] = [];

  selectedRowKeys: any[] = [];

  selectedRows: any[] = [];

  distributorOptions: any[] = [];

  scrollY = 400;

  columns = [
    {
      title: '平台销售单编号',
      dataIndex: 'order_no',
      align: 'center',
      key: 'order_no',
      width: '300',
    },
    {
      title: '经销商',
      dataIndex: 'company_name',
      align: 'center',
      key: 'company_name',
      width: '200',
    },
    {
      title: '省份',
      dataIndex: 'province',
      align: 'center',
      key: 'province',
      width: '200',
    },
    {
      title: '订单金额',
      dataIndex: 'amount',
      align: 'center',
      key: 'amount',
      width: '200',
    },
    {
      title: '下单日期',
      dataIndex: 'created_at',
      width: '200',
      key: 'created_at',
      align: 'center',
    },
  ];

  pagination: any = {
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: this.$store.state.pageSize.pageSize,
    showTotal: () => `共${this.pagination.total}条`,
    onChange: this.paginationChange,
    onShowSizeChange: this.onShowSizeChange,
  };

  mounted() {
    window.addEventListener('resize', this.calcScrollY);
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.calcScrollY);
  }

  onDistributorSearch(companyName: string) {
    this.loadDistributorOptions(companyName);
  }

  async loadDistributorOptions(companyName = '') {
    const res: any = await this.supplyService.getDistributorSearch({
      company_name: companyName,
    });
    const list = res || [];
    this.distributorOptions = list.map((item: any) => ({
      label: item.name,
      value: item.sd_code,
    }));
  }

  // 纵向最大高度按当前页面高度动态计算：视口高度 - 抽屉头部/检索区/表头/分页/底部按钮栏等占用
  calcScrollY() {
    const offset = 350;
    this.scrollY = Math.max(window.innerHeight - offset, 200);
  }

  open() {
    this.filterForm = {
      platformSalesOrderNo: '',
      province: undefined,
      dealer: '',
      timeRange: [],
    };
    this.selectedRowKeys = [];
    this.pagination.current = 1;
    this.visible = true;
    this.calcScrollY();
    this.getProvinces();
    this.getList();
    this.loadDistributorOptions();
  }

  async getProvinces() {
    if (this.provinces.length) {
      return;
    }
    const res = await this.regionService.allRegions();
    if (res && res.length) {
      this.provinces = [...res];
    }
  }

  async getList() {
    // TODO: 调用可生成采购单的订单列表接口，参数：this.filterForm、this.pagination.current
    // 时间起止：this.filterForm.timeRange[0]、this.filterForm.timeRange[1]
    const params = {
      page: this.pagination.current,
      pageSize: this.pagination.pageSize,
      order_no: this.filterForm.platformSalesOrderNo,
      distributor_code: this.filterForm.distributor_code,
      province: this.filterForm.province,
      dealer: this.filterForm.dealer,
      start_time: this.filterForm.timeRange[0],
      end_time: this.filterForm.timeRange[1],
    };
    const res = await this.supplyService.getPlatCollectibleOrders(params);
    console.log('this.filterForm: ', res);
    this.loading = false;
    this.dataSource = res.list;

    this.pagination.total = res.total;
  }

  search() {
    this.pagination.current = 1;
    this.getList();
  }

  resetQuery() {
    this.filterForm = {
      platformSalesOrderNo: '',
      province: undefined,
      dealer: '',
      timeRange: [],
    };
    this.search();
  }

  paginationChange(page: number, pageSize: number) {
    this.pagination.current = page;
    this.pagination.pageSize = pageSize;
    this.getList();
  }

  onShowSizeChange(current: number, size: number) {
    this.pagination.current = 1;
    this.pagination.pageSize = size;
    this.getList();
  }

  onSelectChange(selectedRowKeys: any[], selectedRows: any[]) {
    const keySet = new Set(selectedRowKeys);
    const merged = this.selectedRows.filter(
      (row: any) => keySet.has(row.order_no),
      // eslint-disable-next-line function-paren-newline
    );
    const mergedKeys = new Set(merged.map((row: any) => row.order_no));
    selectedRows.forEach((row: any) => {
      if (!mergedKeys.has(row.order_no)) {
        merged.push(row);
        mergedKeys.add(row.order_no);
      }
    });
    this.selectedRows = merged;
    this.selectedRowKeys = selectedRowKeys;
  }

  // 清空已勾选项（翻页/检索不会清空，仅此按钮与关闭/提交时重置）
  clearSelection() {
    this.selectedRowKeys = [];
    this.selectedRows = [];
  }

  @Ref('generateModal')
  generateModal!: GeneratePurchaseOrderModal;

  // 点击生成：拉取归集订单商品列表后弹出生成采购单弹窗（弹窗内按同一物料号汇总）
  async submit() {
    if (this.selectedRowKeys.length === 0) {
      this.$message.warning('请至少勾选一条记录');
      return;
    }
    this.loading = true;
    try {
      const res: any =
        await this.supplyService.getPlatCollectibleOrderCommodity({
          order_nos: this.selectedRowKeys,
        });
      if (!res) {
        return;
      }
      const list = Array.isArray(res) ? res : res.list || [];
      this.generateModal.open(list);
    } finally {
      this.loading = false;
    }
  }

  // 弹窗内选择供应商、录入采购单价后确认生成
  async onGenerateConfirm(payload: any) {
    this.generateModal.loading = true;
    try {
      const params = {
        order_nos: this.selectedRowKeys,
        supplier_no: payload.supplier_no,
        commodity: payload.commodities.map((item: any) => ({
          commodity_code: item.commodity_code,
          amount: item.platPurchasePrice,
        })),
      };
      const res = await this.supplyService.postPlatCollect(params);
      if (res) {
        this.$message.success('生成成功');
        this.generateModal.onClose();
        this.$emit('refresh');
        this.onClose();
      }
    } finally {
      this.generateModal.loading = false;
    }
  }

  onClose() {
    this.visible = false;
    if (this.generateModal) {
      this.generateModal.onClose();
    }
    this.selectedRowKeys = [];
    this.selectedRows = [];
    this.dataSource = [];
    this.pagination.current = 1;
    this.pagination.total = 0;
    this.pagination.pageSize = 10;
  }
}
